import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getAllDatesFromSelection } from "../../../../Utils/StayUpToDate/RenderComponent";
import {
  PRODUCT_FEED_DAY_OPTIONS,
  PRODUCT_FEED_TIMEFRAMES,
  type ProductFeedTimeframe,
} from "./productFeedSelection";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Recent Content timeframes include All (unlimited); Product Feed does not. */
export type ContentFeedTimeframe = "all" | ProductFeedTimeframe;

export const CONTENT_FEED_TIMEFRAMES: Array<{
  id: ContentFeedTimeframe;
  label: string;
}> = [
  { id: "all", label: "All" },
  ...PRODUCT_FEED_TIMEFRAMES,
];
export const CONTENT_FEED_DAY_OPTIONS = PRODUCT_FEED_DAY_OPTIONS;
export const DEFAULT_CONTENT_FEED_TIMEFRAME: ContentFeedTimeframe = "all";

export type ContentFeedTimeframeDetails = {
  timeframe: ContentFeedTimeframe;
  selectedDays?: string[];
};

export type ContentFeedDateWindow = {
  /** When null, loaders should use legacy (no date filter / story hardcoded 7d). */
  applyFilter: boolean;
  startDate: string | null;
  endDate: string | null;
  selectedDates: string[] | null;
  componentType: "daywise" | null;
  daysNumber: number[];
  selectedDays: string[];
};

const TIMEFRAME_IDS = new Set<string>(
  CONTENT_FEED_TIMEFRAMES.map((item) => item.id)
);

function userTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function isDayWiseContentTimeframe(
  timeframe: ContentFeedTimeframe | null | undefined
): boolean {
  return timeframe === "by-the-day";
}

/** Thursday | Thursday & Friday | Thursday, Friday & Saturday */
export function formatContentFeedSelectedDaysLabel(
  selectedDays: string[] = []
): string {
  const days = (selectedDays || []).map((d) => String(d).trim()).filter(Boolean);
  if (days.length === 0) return "";
  if (days.length === 1) return days[0];
  if (days.length === 2) return `${days[0]} & ${days[1]}`;
  return `${days.slice(0, -1).join(", ")} & ${days[days.length - 1]}`;
}

/** Short timeframe-only label (All / Yesterday / This Week / …). Prefer preview helper below. */
export function getContentFeedTypeLabel(
  timeframe: ContentFeedTimeframe,
  selectedDays: string[] = []
): string {
  if (timeframe === "by-the-day") {
    if (!selectedDays.length) return "By the Day";
    return formatContentFeedSelectedDaysLabel(selectedDays);
  }
  return (
    CONTENT_FEED_TIMEFRAMES.find((item) => item.id === timeframe)?.label ||
    timeframe
  );
}

/**
 * Preview eyebrow: timeframe + content noun.
 * e.g. "Yesterday's Instagram Posts & Reels", "Thursday, Friday & Saturday's Content"
 * All → content noun only (no date window).
 */
export function getContentFeedPreviewTypeLabel(
  timeframe: ContentFeedTimeframe | null | undefined,
  contentTitle: string,
  selectedDays: string[] = []
): string {
  // Strip prior timeframe prefixes so re-applying never stacks
  // ("This Week's This Week's Instagram…").
  const noun =
    String(contentTitle || "")
      .replace(/^Latest\s+/i, "")
      .replace(/^Yesterday'?s\s+/i, "")
      .replace(/^This Week'?s\s+/i, "")
      .replace(/^This Month'?s\s+/i, "")
      .replace(
        /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)(?:(?:\s*&\s*|\s*,\s*)(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday))*'?s\s+/i,
        "",
      )
      .trim() || "Content";

  if (!timeframe || timeframe === "all") {
    const raw = String(contentTitle || "").trim();
    // If caller already passed a timeframe-prefixed title, keep a clean All title.
    if (/^(Yesterday'?s|This Week'?s|This Month'?s)\s+/i.test(raw)) {
      return `Latest ${noun}`.trim();
    }
    return raw || noun || "Content";
  }

  if (timeframe === "by-the-day") {
    const daysLabel = formatContentFeedSelectedDaysLabel(selectedDays);
    if (!daysLabel) return noun;
    return `${daysLabel}'s ${noun}`;
  }
  if (timeframe === "yesterday") return `Yesterday's ${noun}`;
  if (timeframe === "this-week") return `This Week's ${noun}`;
  if (timeframe === "this-month") return `This Month's ${noun}`;
  return noun;
}

/** Serialize timeframe for stayuptodate_components.details */
export function getContentFeedTimeframeStorageDetails(
  timeframe: ContentFeedTimeframe,
  selectedDays: string[] = []
): string {
  const payload: ContentFeedTimeframeDetails =
    timeframe === "by-the-day"
      ? { timeframe, selectedDays: [...selectedDays] }
      : { timeframe };
  return JSON.stringify(payload);
}

function tryParseDetailsJson(raw: unknown): ContentFeedTimeframeDetails | null {
  if (raw == null || raw === "") return null;
  let value: any = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '""') return null;
    try {
      value = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const timeframe = String(value.timeframe || "")
    .toLowerCase()
    .trim() as ContentFeedTimeframe;
  if (!TIMEFRAME_IDS.has(timeframe)) return null;
  const selectedDays = Array.isArray(value.selectedDays)
    ? value.selectedDays.map((d: any) => String(d)).filter(Boolean)
    : [];
  return { timeframe, selectedDays };
}

/**
 * Missing / unparseable details → null (legacy unlimited / story hardcoded 7d).
 */
export function parseContentFeedTimeframeFromComponent(
  component: any
): ContentFeedTimeframeDetails | null {
  return tryParseDetailsJson(component?.details);
}

export function contentFeedTimeframesEqual(
  a: ContentFeedTimeframeDetails | null,
  b: ContentFeedTimeframeDetails | null
): boolean {
  if (!a || !b) return false;
  if (a.timeframe !== b.timeframe) return false;
  if (a.timeframe !== "by-the-day") return true;
  const left = [...(a.selectedDays || [])].map(String).sort().join(",");
  const right = [...(b.selectedDays || [])].map(String).sort().join(",");
  return left === right;
}

/**
 * Build SQL date window mirroring Product Feed / useLoadYesterdayLinks.
 * When details is null (legacy), applyFilter is false.
 */
export function getContentFeedDateWindow(
  details: ContentFeedTimeframeDetails | null | undefined,
  tz: string = userTimezone()
): ContentFeedDateWindow {
  const empty: ContentFeedDateWindow = {
    applyFilter: false,
    startDate: null,
    endDate: null,
    selectedDates: null,
    componentType: null,
    daysNumber: [],
    selectedDays: [],
  };
  if (!details?.timeframe) return empty;

  const timeframe = details.timeframe;
  const selectedDays = details.selectedDays || [];

  // Explicit "All" timeframe = unlimited (same as legacy empty details).
  if (timeframe === "all") {
    return empty;
  }

  if (timeframe === "by-the-day") {
    if (!selectedDays.length) {
      return {
        ...empty,
        applyFilter: true,
        componentType: "daywise",
        selectedDays: [],
      };
    }
    const dates = getAllDatesFromSelection(selectedDays);
    const selectedDates = dates.map((dateStr) =>
      dayjs(new Date(dateStr)).tz(tz).format("YYYY-MM-DD")
    );
    if (!selectedDates.length) {
      const today = dayjs().tz(tz);
      return {
        applyFilter: true,
        startDate: today.clone().subtract(7, "day").format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
        selectedDates: null,
        componentType: "daywise",
        daysNumber: [],
        selectedDays,
      };
    }
    const sorted = [...selectedDates].sort();
    return {
      applyFilter: true,
      startDate: sorted[0],
      endDate: sorted[sorted.length - 1],
      selectedDates,
      componentType: "daywise",
      daysNumber: [],
      selectedDays,
    };
  }

  const today = dayjs().tz(tz);
  if (timeframe === "this-month") {
    return {
      applyFilter: true,
      startDate: today.clone().subtract(1, "month").format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
      selectedDates: null,
      componentType: null,
      daysNumber: [30],
      selectedDays: [],
    };
  }
  if (timeframe === "this-week") {
    return {
      applyFilter: true,
      startDate: today.clone().subtract(7, "day").format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
      selectedDates: null,
      componentType: null,
      daysNumber: [7],
      selectedDays: [],
    };
  }
  // yesterday
  const target = today.clone().subtract(1, "day");
  return {
    applyFilter: true,
    startDate: target.format("YYYY-MM-DD"),
    endDate: target.format("YYYY-MM-DD"),
    selectedDates: null,
    componentType: null,
    daysNumber: [1],
    selectedDays: [],
  };
}

export function getContentFeedDateWindowFromComponent(
  component: any,
  tz?: string
): ContentFeedDateWindow {
  return getContentFeedDateWindow(
    parseContentFeedTimeframeFromComponent(component),
    tz
  );
}

/** Stable React Query / memo dependency for component.details timeframe. */
export function contentFeedDetailsKey(component: any): string {
  return `${component?.id || ""}:${String(component?.details ?? "")}`;
}

/**
 * Merge timeframe into editor component.
 * - Legacy (no stored timeframe): set details only — do NOT clear pins/custom order.
 * - Same timeframe: keep curation.
 * - Timeframe actually changed: clear curation (same as Product Feed / platform change).
 */
export function applyContentFeedTimeframeToComponent(
  opened: any,
  timeframe: ContentFeedTimeframe,
  selectedDays: string[] = []
): any {
  if (!opened) return opened;
  const nextDetails = getContentFeedTimeframeStorageDetails(
    timeframe,
    selectedDays
  );
  const openedParsed = parseContentFeedTimeframeFromComponent(opened);
  const nextParsed = tryParseDetailsJson(nextDetails);
  // First timeframe on a legacy carousel, or unchanged → preserve curation.
  if (!openedParsed || contentFeedTimeframesEqual(openedParsed, nextParsed)) {
    return { ...opened, details: nextDetails };
  }
  return {
    ...opened,
    details: nextDetails,
    post_ids: "",
    removed_postids: "",
    custom_order: "",
    pinned_posts: null,
  };
}
